import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Construction, CornerDownLeft, Search, MessageSquareWarning } from "lucide-react";
import { previewFeatureFor } from "@hisense/shared";
import { navFlatFor } from "@/app/nav";
import { useAuth } from "@/app/auth";
import { cn } from "@/lib/cn";

export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const role = user?.role ?? "viewer";
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Lọc y như sidebar: không lọc thì bảng lệnh trở thành cửa sau vào đúng
    // những trang vừa bị ẩn.
    const items = navFlatFor(role, user?.portalSupplierId, user?.access).map((i) => ({ ...i, label: t(`nav.${i.key}`) }));
    if (!q) return items;
    return items.filter((i) => i.label.toLowerCase().includes(q));
  }, [query, t, role, user?.portalSupplierId]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  if (!open) return null;

  const go = (to: string) => {
    navigate(to);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/25 px-4 pt-[12vh] backdrop-blur-[1px]"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-lg border border-line bg-surface shadow-[var(--shadow-pop)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("common.search")}
      >
        <div className="flex items-center gap-2.5 border-b border-line px-3.5">
          <Search className="size-4 text-ink-faint" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, results.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                const sel = results[active];
                if (sel) go(sel.to);
              } else if (e.key === "Escape") {
                onClose();
              }
            }}
            placeholder={t("common.searchPlaceholder")}
            className="h-12 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
          />
          <kbd className="rounded-xs border border-line bg-sunken px-1.5 py-0.5 text-2xs text-ink-faint">
            ESC
          </kbd>
        </div>
        <ul className="max-h-[46vh] overflow-y-auto p-1.5">
          {results.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-ink-faint">{t("common.noMatches")}</li>
          )}
          {results.map((item, i) => {
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(item.to)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-left text-sm",
                    i === active ? "bg-brand-tint text-brand-ink" : "text-ink-soft",
                  )}
                >
                  <Icon className="size-4 shrink-0 text-ink-faint" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{item.label}</span>
                    {!!item.reviewRuleIds?.length && <span className="flex items-center gap-1 text-2xs text-warn"><MessageSquareWarning aria-hidden="true" className="size-3"/>{t("nav.pendingReview")}</span>}
                    {item.comingSoon && <span className="block text-2xs text-ink-faint">{t("nav.inDevelopment")}</span>}
                    {!!previewFeatureFor(item.to, "page") && <span className="flex items-center gap-1 text-2xs text-info"><Construction aria-hidden="true" className="size-3"/>{t("nav.inProgress")}</span>}
                  </span>
                  {i === active && <CornerDownLeft className="size-3.5 text-brand" />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
